import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig, AUTH_ENABLED } from './msalConfig';

// Singleton MSAL instance. Created lazily so that disabling auth doesn't even initialize it.
let _instance: PublicClientApplication | null = null;

export function getMsalInstance(): PublicClientApplication {
  if (!AUTH_ENABLED) {
    throw new Error('MSAL is disabled (VITE_AUTH_MODE=disabled). Cannot get instance.');
  }
  if (!_instance) {
    _instance = new PublicClientApplication(msalConfig);

    // When a login completes, mark the freshly-logged-in account as the active one.
    // Without this, getAccount() returns null even after successful login.
    _instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        'account' in event.payload &&
        event.payload.account
      ) {
        _instance?.setActiveAccount(event.payload.account);
      }
    });
  }
  return _instance;
}

// Initialize MSAL. Must be called before render. Returns null if auth is disabled.
export async function initializeMsal(): Promise<PublicClientApplication | null> {
  if (!AUTH_ENABLED) return null;

  const instance = getMsalInstance();
  await instance.initialize();

  // Handle redirect response if we just came back from a login.
  const response = await instance.handleRedirectPromise();
  if (response?.account) {
    instance.setActiveAccount(response.account);
  } else {
    // No fresh redirect — set first cached account as active if any.
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }

  return instance;
}